import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '../auth/AuthContext';
import { isAssetOwnerLoginType, isBusinessClientLogin, isBusinessWorkerLogin, canBookAssets } from '../auth/userLoginType';
import { getStoredLoginIdentifier, getSessionToken } from '../auth/sessionToken';
import {
  PROFILE_HUB_SOCIAL,
} from '../constants/profileHubLinks';
import { getClientLegalDocuments } from '../constants/clientLegalContent';
import {
  ProfileHubLegalDocumentPanel,
  ProfileHubTermsPanel,
} from '../components/ProfileHubTermsPanel';
import { ProfileHubPaymentContent } from '../components/ProfileHubPaymentContent';
import { ProfileHubPaymentMethodsContent } from '../components/ProfileHubPaymentMethodsContent';
import { ProfileEditPanel } from '../components/ProfileEditPanel';
import { ProfilePaymentEditPanel } from '../components/ProfilePaymentEditPanel';
import { ProfileLoadCreditsPanel } from '../components/ProfileLoadCreditsPanel';
import { ProfilePaymentMethodsEditPanel } from '../components/ProfilePaymentMethodsEditPanel';
import { ProfileVehiclesEditPanel } from '../components/ProfileVehiclesEditPanel';
import { ProfileAvatarCropPanel } from '../components/ProfileAvatarCropPanel';
import { ImageSourcePicker, type ImageSourcePickerHandle } from '../components/ImageSourcePicker';
import pageThemeStyles from '../components/menuPageTheme.module.css';
import styles from '../components/ProfileMenu.module.css';
import { useI18n } from '../i18n';
import { useAppNavigation } from '../navigation/AppNavigationContext';
import { useUserLocationContext } from '../context/UserLocationContext';
import { fetchProfile, postProfileDeactivate } from '../api/profileApi';
import { AssetRequestError } from '../api/getAssetById';
import { extractProfileDisplaySummary, formatProfileDateOfBirthDisplay, type ProfileDisplaySummary } from '../utils/profileDisplaySummary';
import { incompletePrivateClientUserInfoKeysFromSummary, type PrivateClientUserInfoKey } from '../utils/privateClientUserInfoProblems';
import { ProfileUserInfoValue } from '../components/ProfileUserInfoValue';
import { migrateLegacyLocalProfileVehicles } from '../utils/profileVehiclesMigrate';
import {
  rememberPrivateClientWalletCredits,
  resolvePrivateClientCreditsFromProfilePayload,
} from '../utils/privateClientCredits';
import { getStoredProfileWalletCredits } from '../utils/profileWalletCreditsStorage';
import {
  getStoredProfilePaymentCards,
  type ProfileSavedPaymentCard,
} from '../utils/profilePaymentCard';
import { formatIsraeliPhoneDisplay } from '../utils/phoneE164';
import {
  cacheProfileAvatarFromServer,
  loadLocalProfileAvatarSrc,
  PROFILE_AVATAR_UPDATED_EVENT,
  saveLocalProfileAvatarSrc,
  syncLocalProfileAvatarWithServer,
} from '../utils/profileAvatarStorage';
import { ProfileAvatarImage } from '../components/ProfileAvatarImage';
import { ProfileHubContactPanel } from '../components/ProfileHubContactPanel';
import { ProfileHubDeleteAccountPanel } from '../components/ProfileHubDeleteAccountPanel';
import { BusinessClientWorkersPage } from './BusinessClientWorkersPage';
import { resolveProfileAvatarDisplay } from '../utils/profileAvatarDisplay';
import { resolveProfileLastUpdatedIso } from '../utils/profileLastUpdated';
import { persistWorkerAvatarByEmail } from '../utils/workerAvatarByEmailStorage';
import { uploadProfileAvatarToServer } from '../utils/profileAvatarUpload';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { reverseGeocodeLocalityName } from '../utils/geocodeAddress';
import { israelSettlementDisplayLabel } from '../utils/israelSettlements';
import { defaultAssetsBrowseParams } from '../navigation/AppNavigationContext';
import { snapshotToUrl, type AppHistorySnapshot } from '../navigation/appHistory';

type ProfilePageScreen =
  | 'hub'
  | 'profileMenu'
  | 'paymentMenu'
  | 'paymentMethodsMenu'
  | 'vehiclesMenu'
  | 'loadCredits'
  | 'businessWorkers';

const PROFILE_SCREEN_HISTORY_KEY = 'ofrontendProfileScreen';

type ProfileHistoryState = AppHistorySnapshot & {
  [PROFILE_SCREEN_HISTORY_KEY]?: ProfilePageScreen;
};

function isProfileSubScreen(value: unknown): value is Exclude<ProfilePageScreen, 'hub'> {
  return (
    value === 'profileMenu' ||
    value === 'paymentMenu' ||
    value === 'paymentMethodsMenu' ||
    value === 'vehiclesMenu' ||
    value === 'loadCredits' ||
    value === 'businessWorkers'
  );
}

function readProfileScreenFromHistory(): ProfilePageScreen {
  if (typeof window === 'undefined') {
    return 'hub';
  }
  const state = window.history.state;
  if (!state || typeof state !== 'object') {
    return 'hub';
  }
  const screen = (state as ProfileHistoryState)[PROFILE_SCREEN_HISTORY_KEY];
  return isProfileSubScreen(screen) ? screen : 'hub';
}

function historyViewIsProfile(state: unknown): boolean {
  return Boolean(
    state &&
      typeof state === 'object' &&
      'view' in state &&
      (state as { view?: unknown }).view === 'profile',
  );
}

function profileHubHistorySnapshot(from: unknown): AppHistorySnapshot {
  const base =
    from && typeof from === 'object' ? (from as Partial<AppHistorySnapshot>) : {};
  return {
    view: 'profile',
    authPane: base.authPane ?? 'login',
    assetsBrowseParams: base.assetsBrowseParams
      ? { ...defaultAssetsBrowseParams, ...base.assetsBrowseParams }
      : { ...defaultAssetsBrowseParams },
    searchQuery: base.searchQuery ?? null,
  };
}

/** Push a sub-screen entry on top of a guaranteed profile hub history entry. */
function pushProfileSubScreenHistory(screen: Exclude<ProfilePageScreen, 'hub'>): void {
  if (typeof window === 'undefined') {
    return;
  }
  const hub = profileHubHistorySnapshot(window.history.state);
  const hubUrl = snapshotToUrl(hub);
  // If the current entry is not profile (stale / raced), fix it before pushing.
  if (!historyViewIsProfile(window.history.state)) {
    window.history.replaceState(hub, '', hubUrl);
  }
  window.history.pushState(
    { ...hub, [PROFILE_SCREEN_HISTORY_KEY]: screen } satisfies ProfileHistoryState,
    '',
    hubUrl,
  );
}

function clearProfileSubScreenHistoryEntry(): void {
  if (typeof window === 'undefined') {
    return;
  }
  const state = window.history.state;
  if (!state || typeof state !== 'object') {
    return;
  }
  if (!(PROFILE_SCREEN_HISTORY_KEY in (state as ProfileHistoryState))) {
    return;
  }
  const hub = profileHubHistorySnapshot(state);
  window.history.replaceState(hub, '', snapshotToUrl(hub));
}

type ProfilePageProps = {
  tabFocus: number;
};

export function ProfilePage({ tabFocus }: ProfilePageProps) {
  const { t, language } = useI18n();
  const { userName, userLoginType, isLoggedIn, isAuthReady, logout, updateUserDisplayName } = useAuth();
  const { openAuth, setView, profileHubRowRequest } = useAppNavigation();
  const { status: locationStatus, coordinates: userCoordinates } = useUserLocationContext();
  const menuId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const avatarPickerRef = useRef<ImageSourcePickerHandle>(null);
  const [screen, setScreen] = useState<ProfilePageScreen>('hub');
  const screenRef = useRef<ProfilePageScreen>(screen);
  screenRef.current = screen;
  const [profileSummary, setProfileSummary] = useState<ProfileDisplaySummary | null>(null);
  const [localAvatarSrc, setLocalAvatarSrc] = useState<string | null>(() => loadLocalProfileAvatarSrc());
  const [avatarCropFile, setAvatarCropFile] = useState<File | null>(null);
  const [avatarUploadNotice, setAvatarUploadNotice] = useState<string | null>(null);
  const [avatarDisplayEpoch, setAvatarDisplayEpoch] = useState(0);
  const [expandedHubRow, setExpandedHubRow] = useState<string | null>(null);
  const [expandedHubMessage, setExpandedHubMessage] = useState<string | null>(null);
  const [hubLocality, setHubLocality] = useState<string | null>(null);
  const [paymentCards, setPaymentCards] = useState<ProfileSavedPaymentCard[]>(() =>
    getStoredProfilePaymentCards(getStoredLoginIdentifier()),
  );
  const textDir = language === 'he' ? 'rtl' : 'ltr';

  const toggleHubRow = useCallback((rowId: string, message?: string) => {
    setExpandedHubRow((current) => {
      if (current === rowId) {
        setExpandedHubMessage(null);
        return null;
      }
      setExpandedHubMessage(message ?? null);
      return rowId;
    });
  }, []);

  const loadProfileSummary = useCallback(async (): Promise<ProfileDisplaySummary> => {
    const data = await fetchProfile();
    let summary = extractProfileDisplaySummary(
      data,
      userName,
      getStoredLoginIdentifier(),
      getSessionToken(),
    );
    if (userLoginType === 'private client') {
      summary = {
        ...summary,
        availableCredits: resolvePrivateClientCreditsFromProfilePayload(
          data,
          getStoredLoginIdentifier(),
        ),
      };
    }
    const vehiclesEnabled = canBookAssets(userLoginType);
    if (!vehiclesEnabled) {
      return { ...summary, vehicles: [] };
    }
    const migrated = await migrateLegacyLocalProfileVehicles(
      summary.vehicles,
      getStoredLoginIdentifier(),
      { enabled: true },
    );
    if (migrated && migrated.length > 0) {
      const refreshed = await fetchProfile();
      summary = extractProfileDisplaySummary(
        refreshed,
        userName,
        getStoredLoginIdentifier(),
        getSessionToken(),
      );
      if (userLoginType === 'private client') {
        summary = {
          ...summary,
          availableCredits: resolvePrivateClientCreditsFromProfilePayload(
            refreshed,
            getStoredLoginIdentifier(),
          ),
        };
      }
      if (summary.vehicles.length === 0) {
        summary = { ...summary, vehicles: migrated };
      }
    }
    return summary;
  }, [userName, userLoginType]);

  const applyLoadedProfileSummary = useCallback((summary: ProfileDisplaySummary) => {
    const local = loadLocalProfileAvatarSrc();
    const keepLocalCrop = Boolean(local?.startsWith('data:image/'));
    if (summary.avatarSrc) {
      syncLocalProfileAvatarWithServer(summary.avatarSrc);
    }
    const resolvedLocal = loadLocalProfileAvatarSrc();
    setProfileSummary(
      keepLocalCrop && resolvedLocal
        ? { ...summary, avatarSrc: resolvedLocal }
        : summary,
    );
    if (summary.avatarSrc && !keepLocalCrop) {
      setLocalAvatarSrc(summary.avatarSrc);
    } else if (resolvedLocal) {
      setLocalAvatarSrc(resolvedLocal);
    }
    if (isBusinessWorkerLogin(userLoginType) && summary.avatarSrc && summary.email?.trim()) {
      persistWorkerAvatarByEmail(summary.email, summary.avatarSrc);
    }
  }, [userLoginType]);

  useEffect(() => {
    if (tabFocus < 1) {
      return;
    }
    clearProfileSubScreenHistoryEntry();
    setScreen('hub');
    setExpandedHubRow(null);
    setExpandedHubMessage(null);
    const panel = panelRef.current;
    if (panel) {
      if (typeof panel.scrollTo === 'function') {
        panel.scrollTo({ top: 0, behavior: 'auto' });
      } else {
        panel.scrollTop = 0;
      }
    }
  }, [tabFocus]);

  /** Deep link from the bottom bar (e.g. Contact us) — expand that hub row. */
  useEffect(() => {
    if (!profileHubRowRequest) {
      return;
    }
    setScreen('hub');
    setExpandedHubMessage(null);
    setExpandedHubRow(profileHubRowRequest.rowId);
  }, [profileHubRowRequest]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const onPopState = () => {
      const wasOnSubScreen = screenRef.current !== 'hub';
      const nextScreen = readProfileScreenFromHistory();
      setScreen(nextScreen);
      // Closing a profile sub-screen must not leave the profile tab (history.back race).
      if (wasOnSubScreen && nextScreen === 'hub' && !historyViewIsProfile(window.history.state)) {
        setView('profile', { replace: true });
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [setView]);

  useEffect(() => {
    if (isBusinessWorkerLogin(userLoginType) && screen === 'profileMenu') {
      setScreen('hub');
    }
  }, [userLoginType, screen]);

  useEffect(() => {
    if (!isLoggedIn) {
      setPaymentCards([]);
      return;
    }
    setPaymentCards(getStoredProfilePaymentCards(getStoredLoginIdentifier()));
  }, [isLoggedIn, tabFocus]);

  useEffect(() => {
    if (!isLoggedIn || !isAuthReady || tabFocus < 1) {
      return;
    }
    let cancelled = false;
    // Avoid flashing 0 while GET /profile is in flight — seed from last known wallet.
    if (userLoginType === 'private client') {
      const storedCredits = getStoredProfileWalletCredits(getStoredLoginIdentifier());
      if (storedCredits !== null) {
        setProfileSummary((current) => {
          if (current && typeof current.availableCredits === 'number') {
            return current;
          }
          const base =
            current ??
            extractProfileDisplaySummary(
              null,
              userName,
              getStoredLoginIdentifier(),
              getSessionToken(),
            );
          return { ...base, availableCredits: storedCredits };
        });
      }
    }
    void (async () => {
      try {
        const summary = await loadProfileSummary();
        if (!cancelled) {
          applyLoadedProfileSummary(summary);
        }
      } catch {
        if (!cancelled) {
          const fallback = extractProfileDisplaySummary(
            null,
            userName,
            getStoredLoginIdentifier(),
            getSessionToken(),
          );
          if (userLoginType === 'private client') {
            const storedCredits = getStoredProfileWalletCredits(getStoredLoginIdentifier());
            setProfileSummary({
              ...fallback,
              availableCredits: storedCredits ?? fallback.availableCredits,
            });
          } else {
            setProfileSummary(fallback);
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    applyLoadedProfileSummary,
    isAuthReady,
    isLoggedIn,
    loadProfileSummary,
    tabFocus,
    userLoginType,
    userName,
  ]);

  useEffect(() => {
    if (!isLoggedIn || !isAuthReady || expandedHubRow !== 'profileDetails') {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const summary = await loadProfileSummary();
        if (!cancelled) {
          applyLoadedProfileSummary(summary);
        }
      } catch {
        /* keep current summary; edit screen still refetches */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyLoadedProfileSummary, expandedHubRow, isAuthReady, isLoggedIn, loadProfileSummary]);

  useEffect(() => {
    if (!isLoggedIn) {
      setProfileSummary(null);
      setLocalAvatarSrc(null);
      return;
    }
    setLocalAvatarSrc(loadLocalProfileAvatarSrc());
  }, [isLoggedIn]);

  useEffect(() => {
    const refreshLocalAvatar = () => {
      setLocalAvatarSrc(loadLocalProfileAvatarSrc());
    };
    window.addEventListener(PROFILE_AVATAR_UPDATED_EVENT, refreshLocalAvatar);
    return () => window.removeEventListener(PROFILE_AVATAR_UPDATED_EVENT, refreshLocalAvatar);
  }, []);

  const onHubLogout = useCallback(() => {
    logout();
    openAuth('login');
  }, [logout, openAuth]);

  const onHubEraseAccount = useCallback(async () => {
    if (!isLoggedIn) {
      return;
    }
    if (!window.confirm(t('profile.hub.eraseConfirm'))) {
      return;
    }
    try {
      await postProfileDeactivate();
      logout();
      openAuth('login');
    } catch (err) {
      const message =
        err instanceof AssetRequestError
          ? err.message
          : err instanceof Error
            ? err.message
            : t('common.somethingWrong');
      setExpandedHubRow('accountErase');
      setExpandedHubMessage(message);
    }
  }, [isLoggedIn, logout, openAuth, t]);

  const hubDisplayName =
    profileSummary?.displayName?.trim() || userName?.trim() || t('profile.hub.nameLabel');
  const hubAvatarSrc = resolveProfileAvatarDisplay(profileSummary?.avatarSrc, localAvatarSrc);

  useEffect(() => {
    if (!isLoggedIn) {
      setHubLocality(null);
      return;
    }
    if (locationStatus !== 'ready' || !userCoordinates) {
      return;
    }
    let cancelled = false;
    void (async () => {
      const raw = await reverseGeocodeLocalityName(
        userCoordinates.lat,
        userCoordinates.lng,
        language === 'he' ? 'he' : 'en',
      );
      if (cancelled) {
        return;
      }
      if (!raw) {
        setHubLocality(null);
        return;
      }
      setHubLocality(israelSettlementDisplayLabel(raw, language) || raw);
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, language, locationStatus, userCoordinates]);

  const onProfileLoaded = useCallback(
    (summary: ProfileDisplaySummary) => {
      applyLoadedProfileSummary(summary);
    },
    [applyLoadedProfileSummary],
  );

  const openProfileSubScreen = useCallback((next: Exclude<ProfilePageScreen, 'hub'>) => {
    setExpandedHubRow(null);
    setExpandedHubMessage(null);
    pushProfileSubScreenHistory(next);
    setScreen(next);
  }, []);

  const closeProfileSubScreen = useCallback(() => {
    // Do not history.back() here — it races AppNavigation and can snap to Home.
    // Replace the sub-screen history entry with a clean profile hub entry instead.
    if (
      typeof window !== 'undefined' &&
      isProfileSubScreen(
        (window.history.state as ProfileHistoryState | null)?.[PROFILE_SCREEN_HISTORY_KEY],
      )
    ) {
      clearProfileSubScreenHistoryEntry();
    }
    setScreen('hub');
  }, []);

  const onProfileSaved = useCallback(
    (summary: ProfileDisplaySummary) => {
      setProfileSummary(summary);
      if (summary.displayName?.trim()) {
        updateUserDisplayName(summary.displayName.trim());
      }
      setExpandedHubRow('profileDetails');
      setExpandedHubMessage(t('profile.hub.detailsUpdated'));
      closeProfileSubScreen();
    },
    [closeProfileSubScreen, t, updateUserDisplayName],
  );

  const onPaymentSaved = useCallback(
    (summary: ProfileDisplaySummary) => {
      setProfileSummary(summary);
      setExpandedHubRow('payment');
      setExpandedHubMessage(t('profile.hub.paymentUpdated'));
      closeProfileSubScreen();
    },
    [closeProfileSubScreen, t],
  );

  const onAvatarUpdated = useCallback((avatarSrc: string) => {
    setLocalAvatarSrc(avatarSrc);
    saveLocalProfileAvatarSrc(avatarSrc);
    setProfileSummary((current) => (current ? { ...current, avatarSrc } : current));
  }, []);

  const openProfileEditor = useCallback(() => {
    if (isBusinessWorkerLogin(userLoginType)) {
      return;
    }
    openProfileSubScreen('profileMenu');
  }, [openProfileSubScreen, userLoginType]);

  const openPaymentEditor = useCallback(() => {
    openProfileSubScreen('paymentMenu');
  }, [openProfileSubScreen]);

  const openPaymentMethodsEditor = useCallback(() => {
    openProfileSubScreen('paymentMethodsMenu');
  }, [openProfileSubScreen]);

  const openVehiclesEditor = useCallback(() => {
    openProfileSubScreen('vehiclesMenu');
  }, [openProfileSubScreen]);

  const openLoadCredits = useCallback(() => {
    openProfileSubScreen('loadCredits');
  }, [openProfileSubScreen]);

  const onCreditsLoaded = useCallback((nextCredits: number) => {
    rememberPrivateClientWalletCredits(nextCredits, getStoredLoginIdentifier());
    setProfileSummary((current) =>
      current
        ? { ...current, availableCredits: nextCredits }
        : {
            ...extractProfileDisplaySummary(
              null,
              userName,
              getStoredLoginIdentifier(),
              getSessionToken(),
            ),
            availableCredits: nextCredits,
          },
    );
  }, [userName]);

  const onPaymentMethodsSaved = useCallback((cards: ProfileSavedPaymentCard[]) => {
    setPaymentCards(cards);
    setExpandedHubRow('paymentMethods');
    setExpandedHubMessage(t('profile.hub.paymentMethodsUpdated'));
    closeProfileSubScreen();
  }, [closeProfileSubScreen, t]);

  const onVehiclesSaved = useCallback(
    (summary: ProfileDisplaySummary) => {
      setProfileSummary(summary);
      setExpandedHubRow('vehicles');
      setExpandedHubMessage(t('profile.hub.detailsUpdated'));
      closeProfileSubScreen();
    },
    [closeProfileSubScreen, t],
  );

  const openBusinessWorkers = useCallback(() => {
    openProfileSubScreen('businessWorkers');
  }, [openProfileSubScreen]);

  const onAvatarPick = useCallback(() => {
    avatarPickerRef.current?.open();
  }, []);

  const onAvatarFilePicked = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) {
      return;
    }
    setAvatarCropFile(file);
  }, []);

  const onAvatarCropCancel = useCallback(() => {
    setAvatarCropFile(null);
  }, []);

  const onAvatarCropSave = useCallback(
    (dataUrl: string) => {
      setAvatarCropFile(null);
      setLocalAvatarSrc(dataUrl);
      saveLocalProfileAvatarSrc(dataUrl);
      setProfileSummary((current) => (current ? { ...current, avatarSrc: dataUrl } : current));
      setAvatarDisplayEpoch((n) => n + 1);
      setAvatarUploadNotice(null);
      const previousServerAvatar = profileSummary?.avatarSrc ?? null;
      if (isBusinessWorkerLogin(userLoginType)) {
        const workerEmail =
          profileSummary?.email?.trim() || getStoredLoginIdentifier()?.trim() || null;
        if (workerEmail) {
          persistWorkerAvatarByEmail(workerEmail, dataUrl);
        }
      }
      void uploadProfileAvatarToServer(dataUrl)
        .then((serverAvatarSrc) => {
          const updatedAtIso = resolveProfileLastUpdatedIso(null, getStoredLoginIdentifier());
          setProfileSummary((current) =>
            current ? { ...current, updatedAtIso } : current,
          );
          if (!serverAvatarSrc || serverAvatarSrc === previousServerAvatar) {
            // Keep the cropped photo on screen; server may still be updating or returned stale URL.
            return;
          }
          setLocalAvatarSrc(serverAvatarSrc);
          saveLocalProfileAvatarSrc(serverAvatarSrc);
          setAvatarDisplayEpoch((n) => n + 1);
          setProfileSummary((current) =>
            current ? { ...current, avatarSrc: serverAvatarSrc } : current,
          );
          const workerEmail =
            profileSummary?.email?.trim() ||
            getStoredLoginIdentifier()?.trim() ||
            null;
          if (isBusinessWorkerLogin(userLoginType) && workerEmail) {
            persistWorkerAvatarByEmail(workerEmail, serverAvatarSrc);
          }
        })
        .catch((err) => {
          const message =
            err instanceof Error && err.message.trim()
              ? err.message
              : t('profile.hub.photoUploadFailed');
          setAvatarUploadNotice(message);
        });
    },
    [t, userLoginType, profileSummary?.email, profileSummary?.avatarSrc],
  );

  const hubDetailsSummary =
    profileSummary ??
    (isLoggedIn
      ? extractProfileDisplaySummary(null, userName, getStoredLoginIdentifier(), getSessionToken())
      : null);
  const assetOwnerProfile = isAssetOwnerLoginType(userLoginType);
  const businessClientProfile = isBusinessClientLogin(userLoginType);
  const businessWorkerProfile = isBusinessWorkerLogin(userLoginType);
  const privateClientProfile = userLoginType === 'private client';
  const showVehiclesSection = canBookAssets(userLoginType);
  const showCreditsWallet = canBookAssets(userLoginType);
  const walletCreditsDisplay = (() => {
    if (!showCreditsWallet) {
      return null;
    }
    if (privateClientProfile) {
      const fromSummary = hubDetailsSummary?.availableCredits;
      if (typeof fromSummary === 'number' && Number.isFinite(fromSummary)) {
        return fromSummary;
      }
      // Profile still loading — use cached balance instead of flashing 0.
      return getStoredProfileWalletCredits(getStoredLoginIdentifier());
    }
    const fromSummary = hubDetailsSummary?.availableCredits;
    return typeof fromSummary === 'number' && Number.isFinite(fromSummary) ? fromSummary : null;
  })();

  if (screen === 'profileMenu' && !businessWorkerProfile) {
    return (
      <ProfileEditPanel
        onBack={closeProfileSubScreen}
        onProfileLoaded={onProfileLoaded}
        onSaved={onProfileSaved}
        onAvatarUpdated={onAvatarUpdated}
      />
    );
  }

  if (screen === 'paymentMenu') {
    return (
      <ProfilePaymentEditPanel onBack={closeProfileSubScreen} onSaved={onPaymentSaved} />
    );
  }

  if (screen === 'paymentMethodsMenu') {
    return (
      <ProfilePaymentMethodsEditPanel
        onBack={closeProfileSubScreen}
        onSaved={onPaymentMethodsSaved}
      />
    );
  }

  if (screen === 'vehiclesMenu') {
    return (
      <ProfileVehiclesEditPanel
        onBack={closeProfileSubScreen}
        onSaved={onVehiclesSaved}
        initialVehicles={hubDetailsSummary?.vehicles ?? []}
      />
    );
  }

  if (screen === 'loadCredits') {
    const currentCredits =
      typeof walletCreditsDisplay === 'number' && Number.isFinite(walletCreditsDisplay)
        ? walletCreditsDisplay
        : 0;
    return (
      <ProfileLoadCreditsPanel
        currentCredits={currentCredits}
        onBack={closeProfileSubScreen}
        onLoaded={onCreditsLoaded}
      />
    );
  }

  if (screen === 'businessWorkers') {
    return (
      <BusinessClientWorkersPage
        onBack={closeProfileSubScreen}
        businessId={hubDetailsSummary?.businessId}
        businessName={hubDetailsSummary?.businessName}
      />
    );
  }

  return (
    <div
      className={[pageThemeStyles.wrapEmbedded, pageThemeStyles.wrapThemed].join(' ')}
      data-profile-hub-wrap="true"
      role="region"
      aria-label={t('profile.title')}
    >
      <div
        ref={panelRef}
        className={[pageThemeStyles.panelEmbedded, pageThemeStyles.browsePage].join(' ')}
        data-profile-hub="true"
      >
        <div className={styles.hubRoot} dir={textDir}>
          <div className={styles.hubBannerBackdrop}>
            <header className={styles.hubBanner} aria-label={t('profile.title')}>
              <div className={styles.hubBannerTop}>
                <h1 className={styles.hubBannerTitle}>{t('profile.title')}</h1>
              </div>
              {isLoggedIn ? (
                <div
                  className={styles.hubIdentity}
                  aria-label={hubLocality ? `${hubDisplayName}, ${hubLocality}` : hubDisplayName}
                >
                  <div className={styles.hubIdentityMain}>
                    <div className={styles.hubAvatarWrap}>
                      <ProfileAvatarImage
                        key={`hub-avatar-${avatarDisplayEpoch}`}
                        src={hubAvatarSrc}
                        className={styles.hubAvatar}
                      />
                      <button
                        type="button"
                        className={styles.hubAvatarCameraBtn}
                        aria-label={t('profile.hub.changePhotoAria')}
                        title={t('profile.hub.changePhoto')}
                        onClick={onAvatarPick}
                      >
                        <ProfileCameraIcon />
                      </button>
                      <ImageSourcePicker ref={avatarPickerRef} onFileSelected={onAvatarFilePicked} />
                    </div>
                    {hubLocality ? (
                      <p className={styles.hubLocation}>
                        <ProfileLocationPinIcon className={styles.hubLocationIcon} />
                        <span>{hubLocality}</span>
                      </p>
                    ) : null}
                  </div>
                  {avatarUploadNotice ? (
                    <p className={styles.hubAvatarNoticeError} role="status">
                      {avatarUploadNotice}
                    </p>
                  ) : null}
                </div>
              ) : (
                <div className={styles.hubIdentityGuest} aria-hidden />
              )}
            </header>
          </div>

          {!isLoggedIn ? (
            <div className={styles.hubBody}>
              <p className={pageThemeStyles.browseEmpty} role="status">
                {t('profile.signInBanner')}
              </p>
              <ProfileHubRow
                rtl={language === 'he'}
                label={t('profile.hub.language')}
                icon={<ProfileLanguageIcon />}
                expanded={expandedHubRow === 'language'}
                expandedContent={<LanguageSwitcher variant="profileHub" />}
                onPress={() => toggleHubRow('language')}
              />
            </div>
          ) : (
            <>
              <div className={styles.hubBody}>
              {showCreditsWallet ? (
                <ProfileCreditsWalletCard
                  credits={walletCreditsDisplay}
                  creditsPending={
                    privateClientProfile && profileSummary === null && walletCreditsDisplay === null
                  }
                  language={language}
                  onLoadCredits={privateClientProfile ? openLoadCredits : undefined}
                />
              ) : null}
              {hubDetailsSummary ? (
                <ProfileHubRow
                  rtl={language === 'he'}
                  label={t('profile.hub.detailsTitle')}
                  icon={<ProfileUserIcon />}
                  expanded={expandedHubRow === 'profileDetails'}
                  expandedContent={
                    <ProfileHubDetailsContent
                      summary={hubDetailsSummary}
                      isAssetOwner={assetOwnerProfile}
                      isBusinessClient={businessClientProfile}
                      isBusinessWorker={businessWorkerProfile}
                      onEditProfile={openProfileEditor}
                      successMessage={
                        expandedHubRow === 'profileDetails' ? expandedHubMessage : null
                      }
                    />
                  }
                  onPress={() => toggleHubRow('profileDetails')}
                />
              ) : null}
              {hubDetailsSummary && showVehiclesSection ? (
                <ProfileHubRow
                  rtl={language === 'he'}
                  label={t('profile.vehicles.title')}
                  icon={<ProfileVehiclesIcon />}
                  expanded={expandedHubRow === 'vehicles'}
                  expandedContent={
                    <ProfileHubVehiclesContent
                      vehicles={hubDetailsSummary.vehicles}
                      canEdit
                      onEdit={openVehiclesEditor}
                      successMessage={
                        expandedHubRow === 'vehicles' ? expandedHubMessage : null
                      }
                    />
                  }
                  onPress={() => toggleHubRow('vehicles')}
                />
              ) : null}
              {businessClientProfile ? (
                <ProfileHubRow
                  rtl={language === 'he'}
                  label={t('profile.businessWorkers.title')}
                  icon={<ProfileWorkersIcon />}
                  onClick={openBusinessWorkers}
                />
              ) : null}
              <div className={styles.hubSection}>
                {assetOwnerProfile ? (
                <ProfileHubRow
                  rtl={language === 'he'}
                  rowId="payment"
                  label={t('profile.hub.payment')}
                  icon={<ProfilePaymentIcon />}
                  expanded={expandedHubRow === 'payment'}
                  expandedContent={
                      <ProfileHubPaymentContent
                        payout={hubDetailsSummary?.payout ?? null}
                        onEditPayment={openPaymentEditor}
                        successMessage={
                          expandedHubRow === 'payment' ? expandedHubMessage : null
                        }
                      />
                  }
                  onPress={() => toggleHubRow('payment')}
                />
                ) : null}
                <ProfileHubRow
                  rtl={language === 'he'}
                  rowId="paymentMethods"
                  label={t('profile.hub.paymentMethods')}
                  icon={<ProfilePaymentMethodsIcon />}
                  expanded={expandedHubRow === 'paymentMethods'}
                  expandedContent={
                    <ProfileHubPaymentMethodsContent
                      cards={paymentCards}
                      onEdit={openPaymentMethodsEditor}
                      successMessage={
                        expandedHubRow === 'paymentMethods' ? expandedHubMessage : null
                      }
                    />
                  }
                  onPress={() => toggleHubRow('paymentMethods')}
                />
                {assetOwnerProfile ? (
                <ProfileHubRow
                  rtl={language === 'he'}
                  label={t('profile.hub.terms')}
                  icon={<ProfileTermsIcon />}
                  expanded={expandedHubRow === 'terms'}
                  expandedContent={
                    <ProfileHubTermsPanel termsVariant="assetOwner" />
                  }
                  onPress={() => toggleHubRow('terms')}
                />
                ) : (
                  <>
                    {getClientLegalDocuments(language).map((document) => (
                      <ProfileHubRow
                        key={document.id}
                        rtl={language === 'he'}
                        label={document.heading}
                        icon={<ProfileTermsIcon />}
                        expanded={expandedHubRow === `legal-${document.id}`}
                        expandedContent={
                          <ProfileHubLegalDocumentPanel documentId={document.id} />
                        }
                        onPress={() => toggleHubRow(`legal-${document.id}`)}
                      />
                    ))}
                  </>
                )}
                <ProfileHubRow
                  rtl={language === 'he'}
                  label={t('profile.hub.contact')}
                  icon={<ProfileContactIcon />}
                  expanded={expandedHubRow === 'contact'}
                  expandedContent={<ProfileHubContactPanel />}
                  onPress={() => toggleHubRow('contact')}
                />
                <ProfileHubRow
                  rtl={language === 'he'}
                  label={t('profile.hub.language')}
                  icon={<ProfileLanguageIcon />}
                  expanded={expandedHubRow === 'language'}
                  expandedContent={<LanguageSwitcher variant="profileHub" />}
                  onPress={() => toggleHubRow('language')}
                />
              </div>

              <section className={styles.hubFollowBlock} aria-labelledby={`${menuId}-hub-follow`}>
                <h3 id={`${menuId}-hub-follow`} className={styles.hubSectionTitle}>
                  {t('profile.hub.followUs')}
                </h3>
                <div className={styles.hubSocialRow}>
                  <a
                    className={styles.hubSocialBtn}
                    href={PROFILE_HUB_SOCIAL.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                  >
                    <ProfileInstagramIcon />
                  </a>
                  <a
                    className={styles.hubSocialBtn}
                    href={PROFILE_HUB_SOCIAL.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                  >
                    <ProfileFacebookIcon />
                  </a>
                  <a
                    className={styles.hubSocialBtn}
                    href={PROFILE_HUB_SOCIAL.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="LinkedIn"
                  >
                    <ProfileLinkedInIcon />
                  </a>
                  <a
                    className={styles.hubSocialBtn}
                    href={PROFILE_HUB_SOCIAL.tiktok}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="TikTok"
                  >
                    <ProfileTikTokIcon />
                  </a>
                </div>
              </section>

              <ProfileHubRow
                rtl={language === 'he'}
                label={t('profile.hub.logout')}
                icon={<ProfileLogoutIcon />}
                expanded={expandedHubRow === 'logout'}
                expandedContent={
                  <button type="button" className={styles.hubRowActionBtn} onClick={onHubLogout}>
                    {t('profile.hub.logout')}
                  </button>
                }
                onPress={() => toggleHubRow('logout')}
              />
              <ProfileHubRow
                rtl={language === 'he'}
                label={t('profile.hub.eraseAccount')}
                icon={<ProfileDeleteAccountIcon />}
                expanded={expandedHubRow === 'accountErase'}
                expandedContent={
                  <ProfileHubDeleteAccountPanel
                    onDelete={() => void onHubEraseAccount()}
                    error={expandedHubRow === 'accountErase' ? expandedHubMessage : null}
                  />
                }
                onPress={() => toggleHubRow('accountErase')}
              />
              </div>
            </>
          )}
        </div>
      </div>
      {avatarCropFile ? (
        <ProfileAvatarCropPanel
          file={avatarCropFile}
          onCancel={onAvatarCropCancel}
          onSave={onAvatarCropSave}
        />
      ) : null}
    </div>
  );
}

function ProfileCreditsWalletCard({
  credits,
  creditsPending = false,
  language,
  onLoadCredits,
}: {
  credits: number | null;
  creditsPending?: boolean;
  language: string;
  onLoadCredits?: () => void;
}) {
  const { t } = useI18n();
  const amount = creditsPending
    ? '…'
    : credits === null || credits === undefined || !Number.isFinite(credits)
      ? t('profile.field.notSet')
      : credits.toLocaleString(language === 'he' ? 'he-IL' : 'en-US', {
          maximumFractionDigits: 0,
        });

  return (
    <div
      className={styles.hubWalletCard}
      role="status"
      aria-label={t('profile.hub.walletCreditsAria', { amount })}
      aria-busy={creditsPending || undefined}
      dir={language === 'he' ? 'rtl' : 'ltr'}
    >
      <div className={styles.hubWalletLeading}>
        <span className={styles.hubWalletIconWrap} aria-hidden>
          <ProfileWalletIcon />
        </span>
        <div className={styles.hubWalletMain}>
          <p className={styles.hubWalletTitle}>{t('profile.hub.myWallet')}</p>
          <p className={styles.hubWalletAmount}>{amount}</p>
          <p className={styles.hubWalletLabel}>{t('assetField.credits')}</p>
        </div>
      </div>
      {onLoadCredits ? (
        <button
          type="button"
          className={styles.hubWalletLoadBtn}
          aria-label={t('profile.hub.loadCredits')}
          onClick={onLoadCredits}
        >
          {t('profile.hub.loadCredits')}
        </button>
      ) : null}
    </div>
  );
}

function ProfileWalletIcon() {
  return (
    <svg
      className={styles.hubWalletIcon}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
    >
      <rect
        x="2.75"
        y="7"
        width="18.5"
        height="12.5"
        rx="2.25"
        stroke="currentColor"
        strokeWidth="1.75"
      />
      <path
        d="M2.75 9.25h14.25a2 2 0 0 0 0-4H6.5A3.75 3.75 0 0 0 2.75 9v0"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <path
        d="M15.25 13.25h5.25a1.75 1.75 0 0 1 0 3.5h-5.25a1.75 1.75 0 0 1 0-3.5z"
        fill="currentColor"
        opacity="0.95"
      />
      <circle cx="16.35" cy="15" r="0.85" fill="#623dff" />
    </svg>
  );
}

function ProfileHubVehiclesContent({
  vehicles,
  canEdit,
  onEdit,
  successMessage,
}: {
  vehicles: ProfileDisplaySummary['vehicles'];
  canEdit: boolean;
  onEdit: () => void;
  successMessage?: string | null;
}) {
  const { t, language } = useI18n();
  const formatDetailValue = (value: string | null | undefined): string => {
    const trimmed = value?.trim();
    return trimmed ? trimmed : t('profile.field.notSet');
  };

  return (
    <div
      className={[
        styles.hubDetailsPanel,
        language === 'he' ? styles.hubDetailsPanelRtl : styles.hubDetailsPanelLtr,
      ]
        .filter(Boolean)
        .join(' ')}
      dir={language === 'he' ? 'rtl' : 'ltr'}
    >
      {successMessage ? (
        <p className={styles.hubPaymentUpdatedNotice} role="status">
          {successMessage}
        </p>
      ) : null}
      {canEdit ? (
        <button
          type="button"
          className={styles.hubDetailsEditBtn}
          onClick={onEdit}
          aria-label={t('profile.hub.editVehicles')}
        >
          <ProfileEditIcon />
          <span>{t('list.edit')}</span>
        </button>
      ) : null}
      {vehicles.length > 0 ? (
        <div className={styles.hubVehiclesBlocks}>
          {vehicles.map((car, index) => (
            <section key={car.id} className={styles.hubVehicleBlock} aria-labelledby={`hub-vehicle-${car.id}`}>
              <div className={styles.hubVehicleBlockTitleRow}>
                <h3 id={`hub-vehicle-${car.id}`} className={styles.hubDetailsTitle}>
                  {t('profile.vehicles.carN', { n: index + 1 })}
                </h3>
                {car.isPrimary ? (
                  <span className={styles.hubVehiclePrimaryBadge}>{t('profile.vehicles.primary')}</span>
                ) : null}
              </div>
              <dl className={styles.hubDetailsList}>
                <div className={styles.hubDetailRow}>
                  <dt className={styles.hubDetailLabel}>{t('profile.vehicles.manufacturer')}</dt>
                  <dd className={styles.hubDetailValue}>{formatDetailValue(car.manufacturer)}</dd>
                </div>
                <div className={styles.hubDetailRow}>
                  <dt className={styles.hubDetailLabel}>{t('profile.vehicles.model')}</dt>
                  <dd className={styles.hubDetailValue}>{formatDetailValue(car.model)}</dd>
                </div>
                <div className={styles.hubDetailRow}>
                  <dt className={styles.hubDetailLabel}>{t('profile.vehicles.color')}</dt>
                  <dd className={styles.hubDetailValue}>{formatDetailValue(car.color)}</dd>
                </div>
                <div className={styles.hubDetailRow}>
                  <dt className={styles.hubDetailLabel}>{t('profile.vehicles.licenseNumber')}</dt>
                  <dd className={[styles.hubDetailValue, styles.hubDetailValueLtr].join(' ')}>
                    {formatDetailValue(car.licenseNumber)}
                  </dd>
                </div>
              </dl>
            </section>
          ))}
        </div>
      ) : (
        <p className={styles.hubVehiclesEmpty}>{t('profile.vehicles.noneOnFile')}</p>
      )}
    </div>
  );
}

function ProfileHubDetailsContent({
  summary,
  isAssetOwner,
  isBusinessClient,
  isBusinessWorker,
  onEditProfile,
  successMessage,
}: {
  summary: ProfileDisplaySummary;
  isAssetOwner: boolean;
  isBusinessClient: boolean;
  isBusinessWorker: boolean;
  onEditProfile: () => void;
  successMessage?: string | null;
}) {
  const { t, language } = useI18n();
  const employerManagedId = useId();
  const dobDisplay = formatProfileDateOfBirthDisplay(summary.dateOfBirth);
  const businessStyleProfile = isAssetOwner || isBusinessClient;
  const problemKeys = new Set(incompletePrivateClientUserInfoKeysFromSummary(summary));
  const formatDetailValue = (value: string | null | undefined, isPhone = false): string => {
    const trimmed = value?.trim();
    if (!trimmed) {
      return t('profile.field.notSet');
    }
    return isPhone ? formatIsraeliPhoneDisplay(trimmed) : trimmed;
  };
  const formatCreditsValue = (credits: number | null | undefined): string => {
    if (credits === null || credits === undefined || !Number.isFinite(credits)) {
      return t('profile.field.notSet');
    }
    return credits.toLocaleString(language === 'he' ? 'he-IL' : 'en-US', {
      maximumFractionDigits: 2,
    });
  };
  const rows = businessStyleProfile
    ? [
        { label: t('assetField.businessName'), value: summary.businessName, isPhone: false, infoKey: null as PrivateClientUserInfoKey | null },
        { label: t('assetField.businessId'), value: summary.businessId, isPhone: false, infoKey: null },
        ...(isBusinessClient
          ? [
              {
                label: t('profile.field.employeeRegistrationCode'),
                value: summary.workerRegistrationCode,
                isPhone: false,
                infoKey: null as PrivateClientUserInfoKey | null,
              },
            ]
          : []),
        { label: t('profile.field.email'), value: summary.email, isPhone: false, infoKey: 'email' as const },
        { label: t('profile.field.firstName'), value: summary.firstName, isPhone: false, infoKey: 'first_name' as const },
        { label: t('profile.field.lastName'), value: summary.lastName, isPhone: false, infoKey: 'last_name' as const },
        { label: t('profile.field.cellPhone'), value: summary.phone, isPhone: true, infoKey: 'phone_number' as const },
        ...(summary.phoneSecondary?.trim()
          ? [{ label: t('assetField.phoneNumber2'), value: summary.phoneSecondary, isPhone: true, infoKey: null as PrivateClientUserInfoKey | null }]
          : []),
      ]
    : [
        { label: t('profile.field.firstName'), value: summary.firstName, isPhone: false, infoKey: 'first_name' as const },
        { label: t('profile.field.lastName'), value: summary.lastName, isPhone: false, infoKey: 'last_name' as const },
        { label: t('profile.field.cellPhone'), value: summary.phone, isPhone: true, infoKey: 'phone_number' as const },
        { label: t('profile.field.email'), value: summary.email, isPhone: false, infoKey: 'email' as const },
        { label: t('profile.field.dateOfBirth'), value: dobDisplay, isPhone: false, infoKey: 'birth_date' as const },
        { label: t('profile.field.occupation'), value: summary.occupation, isPhone: false, infoKey: null as PrivateClientUserInfoKey | null },
      ];

  const showOwnerLogo = Boolean(summary.ownerLogoSrc);
  const logoAfterBusinessName = businessStyleProfile && showOwnerLogo;

  return (
    <div
      className={[
        styles.hubDetailsPanel,
        language === 'he' ? styles.hubDetailsPanelRtl : styles.hubDetailsPanelLtr,
      ]
        .filter(Boolean)
        .join(' ')}
      dir={language === 'he' ? 'rtl' : 'ltr'}
    >
      {successMessage ? (
        <p className={styles.hubPaymentUpdatedNotice} role="status">
          {successMessage}
        </p>
      ) : null}
      {!isBusinessWorker ? (
        <button
          type="button"
          className={styles.hubDetailsEditBtn}
          onClick={onEditProfile}
          aria-label={t('profile.hub.editProfile')}
        >
          <ProfileEditIcon />
          <span>{t('list.edit')}</span>
        </button>
      ) : (
        <p className={styles.hubEmployerManagedHint}>{t('profile.hub.workerProfileReadOnly')}</p>
      )}
      <dl className={styles.hubDetailsList}>
        {rows.flatMap((row, index) => {
          const items = [
            <div key={row.label} className={styles.hubDetailRow}>
              <dt className={styles.hubDetailLabel}>{row.label}</dt>
              <dd
                className={[
                  styles.hubDetailValue,
                  row.isPhone || row.label === t('profile.field.email') ? styles.hubDetailValueLtr : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <ProfileUserInfoValue
                  text={formatDetailValue(row.value, row.isPhone)}
                  problem={Boolean(row.infoKey && problemKeys.has(row.infoKey))}
                />
              </dd>
            </div>,
          ];
          if (logoAfterBusinessName && index === 0) {
            items.push(
              <div key="owner-logo" className={styles.hubDetailRow}>
                <dt className={styles.hubDetailLabel}>{t('profile.field.ownerLogo')}</dt>
                <dd className={[styles.hubDetailValue, styles.hubDetailValueLogo].join(' ')}>
                  <img src={summary.ownerLogoSrc!} alt="" className={styles.hubOwnerLogoThumb} />
                </dd>
              </div>,
            );
          }
          return items;
        })}
        {!businessStyleProfile && showOwnerLogo ? (
          <div className={styles.hubDetailRow}>
            <dt className={styles.hubDetailLabel}>{t('profile.field.ownerLogo')}</dt>
            <dd className={[styles.hubDetailValue, styles.hubDetailValueLogo].join(' ')}>
              <img src={summary.ownerLogoSrc!} alt="" className={styles.hubOwnerLogoThumb} />
            </dd>
          </div>
        ) : null}
      </dl>
      {isBusinessWorker ? (
        <div className={styles.hubEmployerManagedBlock} aria-labelledby={employerManagedId}>
          <p id={employerManagedId} className={styles.hubEmployerManagedTitle}>
            {t('profile.hub.employerManaged')}
          </p>
          <dl className={styles.hubDetailsList}>
            <div className={styles.hubDetailRow}>
              <dt className={styles.hubDetailLabel}>{t('profile.field.availableCredits')}</dt>
              <dd className={[styles.hubDetailValue, styles.hubDetailValueLtr, styles.hubDetailValueReadOnly].join(' ')}>
                {formatCreditsValue(summary.availableCredits)}
              </dd>
            </div>
          </dl>
          <p className={styles.hubEmployerManagedHint}>{t('profile.field.availableCreditsHint')}</p>
        </div>
      ) : null}
    </div>
  );
}

function ProfileHubRow({
  label,
  icon,
  onClick,
  onPress,
  danger = false,
  expanded = false,
  expandMessage,
  expandedContent,
  rtl,
}: {
  label: string;
  icon: ReactNode;
  onClick?: () => void;
  onPress?: () => void;
  danger?: boolean;
  expanded?: boolean;
  expandMessage?: string;
  expandedContent?: ReactNode;
  rowId?: string;
  rtl: boolean;
}) {
  const rowClass = [
    styles.hubRow,
    danger ? styles.hubRowDanger : '',
    onClick || onPress ? '' : styles.hubRowStatic,
  ]
    .filter(Boolean)
    .join(' ');
  const showChevron = Boolean(onClick || onPress);
  const content = (
    <>
      <span className={styles.hubRowIcon} aria-hidden>
        {icon}
      </span>
      <span className={styles.hubRowLabel}>{label}</span>
      {showChevron ? (
        <span className={styles.hubRowTrail} aria-hidden>
          <ProfileHubCaret expanded={expanded} rtl={rtl} />
        </span>
      ) : null}
    </>
  );

  const detail =
    expanded && expandedContent ? (
      <div className={styles.hubRowExpandPanel}>{expandedContent}</div>
    ) : expanded && expandMessage ? (
      <p className={styles.hubRowDetail} role="status">
        {expandMessage}
      </p>
    ) : null;

  if (!onClick && !onPress) {
    return (
      <div className={styles.hubRowGroup}>
        <div className={rowClass}>{content}</div>
        {detail}
      </div>
    );
  }

  const handleClick = onPress ?? onClick;
  return (
    <div className={styles.hubRowGroup}>
      <button
        type="button"
        className={rowClass}
        onClick={handleClick}
        aria-expanded={onPress || expandedContent ? expanded : undefined}
      >
        {content}
      </button>
      {detail}
    </div>
  );
}

const PROFILE_ICON = {
  size: 22,
  stroke: 2,
  cap: 'round' as const,
  join: 'round' as const,
};

function ProfileHubCaret({ expanded, rtl }: { expanded: boolean; rtl: boolean }) {
  const caretProps = {
    className: styles.hubRowCaret,
    viewBox: '0 0 24 24',
    width: 14,
    height: 14,
    fill: 'none' as const,
    stroke: 'currentColor',
    strokeWidth: 3.25,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };
  if (expanded) {
    return (
      <svg {...caretProps}>
        <path d="M6 9l6 6 6-6" />
      </svg>
    );
  }
  if (rtl) {
    return (
      <svg {...caretProps}>
        <path d="M9 6l6 6-6 6" />
      </svg>
    );
  }
  return (
    <svg {...caretProps}>
      <path d="M15 6l-6 6 6 6" />
    </svg>
  );
}

function ProfileLocationPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden focusable="false">
      <path
        d="M12 2c3.3 0 6 2.5 6 5.7 0 4.3-6 12.3-6 12.3S6 12 6 7.7C6 4.5 8.7 2 12 2z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <circle cx="12" cy="8" r="2" fill="currentColor" />
    </svg>
  );
}

function ProfileEditIcon() {
  return (
    <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

function ProfileCameraIcon() {
  return (
    <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 7h3l2-3h6l2 3h3a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2z" />
      <circle cx="12" cy="13" r="3.5" />
    </svg>
  );
}

function ProfileUserIcon() {
  return (
    <svg viewBox="0 0 24 24" width={PROFILE_ICON.size} height={PROFILE_ICON.size} fill="none" stroke="currentColor" strokeWidth={PROFILE_ICON.stroke} strokeLinecap={PROFILE_ICON.cap} strokeLinejoin={PROFILE_ICON.join} aria-hidden>
      <circle cx="12" cy="8" r="3.25" />
      <path d="M5.5 20c0-3.6 2.9-6.5 6.5-6.5s6.5 2.9 6.5 6.5" />
    </svg>
  );
}

function ProfileVehiclesIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={PROFILE_ICON.size}
      height={PROFILE_ICON.size}
      fill="none"
      stroke="currentColor"
      strokeWidth={PROFILE_ICON.stroke}
      strokeLinecap={PROFILE_ICON.cap}
      strokeLinejoin={PROFILE_ICON.join}
      aria-hidden
    >
      <path d="M3 14.5h18" />
      <path d="M5 14.5l1.8-4.2A2 2 0 0 1 8.6 9h6.8a2 2 0 0 1 1.8 1.3L19 14.5" />
      <path d="M4 14.5v2.2a1.3 1.3 0 0 0 1.3 1.3H7" />
      <path d="M17 18h1.7a1.3 1.3 0 0 0 1.3-1.3V14.5" />
      <circle cx="8" cy="17.5" r="1.5" />
      <circle cx="16" cy="17.5" r="1.5" />
    </svg>
  );
}

function ProfileWorkersIcon() {
  return (
    <svg viewBox="0 0 24 24" width={PROFILE_ICON.size} height={PROFILE_ICON.size} fill="none" stroke="currentColor" strokeWidth={PROFILE_ICON.stroke} strokeLinecap={PROFILE_ICON.cap} strokeLinejoin={PROFILE_ICON.join} aria-hidden>
      <circle cx="9" cy="8" r="2.75" />
      <circle cx="16.5" cy="9.25" r="2.25" />
      <path d="M4.5 19c0-2.5 2-4.5 4.5-4.5" />
      <path d="M13.5 19c0-1.9 1.5-3.5 3.5-3.5.8 0 1.5.2 2.1.6" />
    </svg>
  );
}

function ProfilePaymentIcon() {
  return (
    <svg viewBox="0 0 24 24" width={PROFILE_ICON.size} height={PROFILE_ICON.size} fill="none" stroke="currentColor" strokeWidth={PROFILE_ICON.stroke} strokeLinecap={PROFILE_ICON.cap} strokeLinejoin={PROFILE_ICON.join} aria-hidden>
      <rect x="2.5" y="5.5" width="19" height="13" rx="2.5" />
      <path d="M2.5 10.5h19" />
      <rect x="5.5" y="14" width="4.5" height="2.5" rx="0.5" />
    </svg>
  );
}

function ProfilePaymentMethodsIcon() {
  return (
    <svg viewBox="0 0 24 24" width={PROFILE_ICON.size} height={PROFILE_ICON.size} fill="none" stroke="currentColor" strokeWidth={PROFILE_ICON.stroke} strokeLinecap={PROFILE_ICON.cap} strokeLinejoin={PROFILE_ICON.join} aria-hidden>
      <rect x="2.5" y="5.5" width="19" height="13" rx="2.5" />
      <path d="M2.5 10.5h19" />
      <path d="M6 15h3.5" />
      <path d="M14.5 15h3.5" />
    </svg>
  );
}

function ProfileTermsIcon() {
  return (
    <svg viewBox="0 0 24 24" width={PROFILE_ICON.size} height={PROFILE_ICON.size} fill="none" stroke="currentColor" strokeWidth={PROFILE_ICON.stroke} strokeLinecap={PROFILE_ICON.cap} strokeLinejoin={PROFILE_ICON.join} aria-hidden>
      <path d="M8 3h8l4 4v14H8V3z" />
      <path d="M16 3v4h4" />
      <path d="M10.5 12h7M10.5 16h7" />
      <circle cx="10.5" cy="8.5" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}

function ProfileContactIcon() {
  return (
    <svg viewBox="0 0 24 24" width={PROFILE_ICON.size} height={PROFILE_ICON.size} fill="none" stroke="currentColor" strokeWidth={PROFILE_ICON.stroke} strokeLinecap={PROFILE_ICON.cap} strokeLinejoin={PROFILE_ICON.join} aria-hidden>
      <path d="M4 6.5h16v11H4z" />
      <path d="M4 7.5l8 6 8-6" />
    </svg>
  );
}

function ProfileLanguageIcon() {
  return (
    <svg viewBox="0 0 24 24" width={PROFILE_ICON.size} height={PROFILE_ICON.size} fill="none" stroke="currentColor" strokeWidth={PROFILE_ICON.stroke} strokeLinecap={PROFILE_ICON.cap} strokeLinejoin={PROFILE_ICON.join} aria-hidden>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17" />
      <path d="M12 3.5c2.2 2.8 3.4 5.8 3.4 8.5S14.2 17.7 12 20.5C9.8 17.7 8.6 14.7 8.6 12S9.8 6.3 12 3.5z" />
    </svg>
  );
}

function ProfileDeleteAccountIcon() {
  return (
    <svg viewBox="0 0 24 24" width={PROFILE_ICON.size} height={PROFILE_ICON.size} fill="none" stroke="currentColor" strokeWidth={PROFILE_ICON.stroke} strokeLinecap={PROFILE_ICON.cap} strokeLinejoin={PROFILE_ICON.join} aria-hidden>
      <path d="M4.5 7h15" />
      <path d="M9.5 7V4.5h5V7" />
      <path d="M6.5 7l1 12.5h9l1-12.5" />
      <path d="M10.5 11v5M13.5 11v5" />
    </svg>
  );
}

function ProfileLogoutIcon() {
  return (
    <svg viewBox="0 0 24 24" width={PROFILE_ICON.size} height={PROFILE_ICON.size} fill="none" stroke="currentColor" strokeWidth={PROFILE_ICON.stroke} strokeLinecap={PROFILE_ICON.cap} strokeLinejoin={PROFILE_ICON.join} aria-hidden>
      <path d="M10 8V6a2 2 0 0 1 2-2h7v16h-7a2 2 0 0 1-2-2v-2" />
      <path d="M3 12h10" />
      <path d="M7 8.5L3 12l4 3.5" />
    </svg>
  );
}

function ProfileFacebookIcon() {
  return (
    <svg viewBox="0 0 24 24" width={24} height={24} fill="currentColor" aria-hidden>
      <path d="M14 8.5h2.5V5H14c-2.4 0-4 1.5-4 4v2H7v3.5h3v7H14v-7h2.8l.4-3.5H14V9.5c0-.8.7-1 1.2-1z" />
    </svg>
  );
}

function ProfileInstagramIcon() {
  return (
    <svg viewBox="0 0 24 24" width={24} height={24} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3.5" y="3.5" width="17" height="17" rx="4.5" />
      <circle cx="12" cy="12" r="3.6" />
      <circle cx="17.2" cy="6.8" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}

function ProfileLinkedInIcon() {
  return (
    <svg viewBox="0 0 24 24" width={24} height={24} fill="currentColor" aria-hidden>
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a-2.116 2.116 0 1 1 0-4.232 2.116 2.116 0 0 1 0 4.232zM7.119 20.452H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function ProfileTikTokIcon() {
  return (
    <svg viewBox="0 0 24 24" width={24} height={24} fill="currentColor" aria-hidden>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
    </svg>
  );
}
